/* eslint-disable import/no-cycle */
/* eslint-disable react/display-name */
import React, { ReactElement } from 'react';
import { Hr, Body } from '@sumup-oss/circuit-ui';
import { Download } from '@sumup-oss/icons';
import { Entry } from 'contentful';
import {
  BLOCKS,
  MARKS,
  INLINES,
  Heading2,
  Heading3,
  Paragraph,
  UnorderedList,
  OrderedList,
  Quote,
  Hyperlink as HyperlinkType,
  EntryLinkBlock,
  AssetLinkBlock,
} from '@contentful/rich-text-types';
import Link from 'next/link';

import { Hyperlink } from 'components/styledComponents';
import {
  AccordionList,
  ArticleText,
  EmbeddedImage,
  EmbeddedImageContainer,
  Heading,
  List,
  ItalicText,
  UnderlinedText,
  EmbeddedDownloadButtonContainer,
  EmbeddedDownloadButton,
} from 'components/styledComponents/richTextOptions';
import AccordionGroup from 'pages/articles/components/AccordionGroup';
import TabGroup from 'pages/articles/components/TabGroup';
import Callout from 'pages/articles/components/Callout';
import {
  AccordionEntryData,
  AccordionProps,
} from 'pages/articles/components/AccordionGroup/AccordionGroup';
import { TabsData } from 'pages/articles/components/TabGroup/TabGroup';
import { CalloutData } from 'pages/articles/components/Callout/Callout';
import { Children, ContentfulItem } from 'types/common';
import {
  determineHrefLocalePrepend,
  formatContentfulHref,
} from 'services/contentful';
import TextBlockGroup from 'pages/articles/components/TextBlockGroup';
import { TextBlockGroupType } from 'pages/articles/components/TextBlockGroup/TextBlockGroup';
import logger from 'services/logger';

const EMBEDDED_ENTRY_CONTENT_TYPES = {
  ACCORDION_GROUP: 'accordionGroup',
  ACCORDION_ENTRY: 'accordionEntry',
  TAB_GROUP: 'tabGroup',
  TAB_ENTRY: 'tabEntry',
  CALLOUT: 'callout',
  TEXT_BLOCK: 'textBlockEntry',
  TEXT_BLOCK_GROUP: 'textBlockGroup',
};

const EMBEDDED_ENTRY_ASSET_TYPES = {
  IMAGE: 'image',
  FILE: 'application',
};

const entryTypesOptions = (
  node: EntryLinkBlock & {
    data: {
      target: Entry<ContentfulItem & AccordionProps & TabsData & CalloutData> &
        AccordionEntryData &
        TextBlockGroupType;
    };
  },
): null | ReactElement => {
  if (!node?.data?.target?.fields) {
    return null;
  }

  const contentType = node?.data?.target?.sys?.contentType?.sys?.id;

  switch (contentType) {
    case EMBEDDED_ENTRY_CONTENT_TYPES.ACCORDION_GROUP:
      return <AccordionGroup accordionGroupData={node?.data?.target} />;
    case EMBEDDED_ENTRY_CONTENT_TYPES.ACCORDION_ENTRY:
      return (
        <AccordionGroup
          accordionGroupData={{
            ...node?.data?.target,
            fields: { name: '', accordionEntries: [node?.data?.target] },
          }}
        />
      );
    case EMBEDDED_ENTRY_CONTENT_TYPES.TAB_GROUP:
      return <TabGroup tabsData={node?.data?.target} />;
    case EMBEDDED_ENTRY_CONTENT_TYPES.TAB_ENTRY:
      return (
        <TabGroup
          tabsData={{
            ...node?.data?.target,
            fields: { name: '', tabEntries: [node?.data?.target] },
          }}
        />
      );
    case EMBEDDED_ENTRY_CONTENT_TYPES.CALLOUT:
      return <Callout calloutData={node?.data?.target} />;
    case EMBEDDED_ENTRY_CONTENT_TYPES.TEXT_BLOCK_GROUP:
      return <TextBlockGroup data={node?.data?.target} />;
    case EMBEDDED_ENTRY_CONTENT_TYPES.TEXT_BLOCK:
      return (
        <TextBlockGroup
          data={{
            ...node?.data?.target,
            fields: {
              name: 'singleBlock',
              linkedTextBlockEntries: [node?.data?.target],
            },
          }}
        />
      );
    default:
      return null;
  }
};

const assetTypesOptions = (
  node: AssetLinkBlock & {
    data: {
      target: {
        fields: {
          file: {
            contentType: string;
            url: string;
            details: {
              image: {
                height: number;
                width: number;
              };
            };
          };
          description: string;
        };
      };
    };
  },
): ReactElement => {
  const contentType = node?.data?.target?.fields?.file?.contentType;
  const imgHeight = node?.data?.target?.fields?.file?.details?.image?.height;
  const imgWidth = node?.data?.target?.fields?.file?.details?.image?.width;

  if (contentType) {
    if (contentType && contentType.includes(EMBEDDED_ENTRY_ASSET_TYPES.IMAGE)) {
      return (
        <EmbeddedImageContainer>
          <EmbeddedImage
            width={imgWidth}
            height={imgHeight}
            alt={node?.data?.target?.fields?.description}
            src={`https:${node?.data?.target?.fields?.file?.url}`}
          />
        </EmbeddedImageContainer>
      );
    }

    if (contentType.includes(EMBEDDED_ENTRY_ASSET_TYPES.FILE)) {
      return (
        <EmbeddedDownloadButtonContainer>
          <EmbeddedDownloadButton
            icon={Download}
            href={`https:${node?.data?.target?.fields?.file?.url}`}
            target="_blank"
            download
          >
            {node?.data?.target?.fields?.description}
          </EmbeddedDownloadButton>
        </EmbeddedDownloadButtonContainer>
      );
    }
  }
  return null;
};

export const RICH_TEXT_RENDERER_OPTIONS = {
  renderNode: {
    [BLOCKS.HEADING_1]: (): null => null,
    [BLOCKS.HEADING_2]: (
      _node: Heading2,
      children: boolean | Children,
    ): ReactElement => (
      <Heading as="h2" size="three">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_3]: (
      _node: Heading3,
      children: boolean | Children,
    ): ReactElement => (
      <Heading as="h3" size="four">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_4]: (): null => null,
    [BLOCKS.HEADING_5]: (): null => null,
    [BLOCKS.HEADING_6]: (): null => null,
    [BLOCKS.PARAGRAPH]: (
      _node: Paragraph,
      children: boolean | Children,
    ): ReactElement => <ArticleText size="m">{children}</ArticleText>,
    [BLOCKS.EMBEDDED_ENTRY]: entryTypesOptions,
    [BLOCKS.UL_LIST]: (
      _node: UnorderedList,
      children: boolean | Children,
    ): ReactElement => <List>{children}</List>,
    [BLOCKS.OL_LIST]: (
      _node: OrderedList,
      children: boolean | Children,
    ): ReactElement => <List variant="ordered">{children}</List>,
    [BLOCKS.QUOTE]: (
      _node: Quote,
      children: boolean | Children,
    ): ReactElement => <p>{children}</p>,
    [BLOCKS.HR]: (): ReactElement => <Hr />,
    // To be determined how to render inline entries
    [INLINES.EMBEDDED_ENTRY]: (): null => null,
    [INLINES.HYPERLINK]: (
      node: HyperlinkType,
      children: boolean | Children,
    ): ReactElement => {
      if (/support./.test(formatContentfulHref(node.data.uri))) {
        logger.warn(
          `Link that points to old support domain: ${formatContentfulHref(
            node.data.uri,
          )}`,
        );
      }

      return (
        <Link
          href={formatContentfulHref(node.data.uri)}
          {...determineHrefLocalePrepend(node.data.uri)}
          passHref
          legacyBehavior
        >
          <Hyperlink
            size="m"
            target={
              /support.|help.|\/article/.test(
                formatContentfulHref(node.data.uri),
              )
                ? '_self'
                : '_blank'
            }
          >
            {children}
          </Hyperlink>
        </Link>
      );
    },
    [BLOCKS.EMBEDDED_ASSET]: assetTypesOptions,
  },
  renderMark: {
    [MARKS.BOLD]: (text: string): ReactElement => (
      <Body as="strong" size="m" weight="semibold">
        {text}
      </Body>
    ),
    [MARKS.ITALIC]: (text: string): ReactElement => (
      <ItalicText as="em" size="m">
        {text}
      </ItalicText>
    ),
    [MARKS.UNDERLINE]: (text: string): ReactElement => (
      <UnderlinedText as="span" size="m">
        {text}
      </UnderlinedText>
    ),
    [MARKS.CODE]: (text: string): ReactElement => (
      <Body as="span" size="m">
        <code>{text}</code>
      </Body>
    ),
  },
};

export const ACCORDION_OPTIONS = {
  ...RICH_TEXT_RENDERER_OPTIONS,
  renderNode: {
    ...RICH_TEXT_RENDERER_OPTIONS.renderNode,
    [BLOCKS.UL_LIST]: (
      _node: UnorderedList,
      children: boolean | Children,
    ): ReactElement => <AccordionList>{children}</AccordionList>,
    [BLOCKS.OL_LIST]: (
      _node: OrderedList,
      children: boolean | Children,
    ): ReactElement => (
      <AccordionList variant="ordered">{children}</AccordionList>
    ),
  },
};

export const CALLOUT_OPTIONS = {
  ...RICH_TEXT_RENDERER_OPTIONS,
  renderNode: {
    ...RICH_TEXT_RENDERER_OPTIONS.renderNode,
    [BLOCKS.PARAGRAPH]: (
      _node: Paragraph,
      children: boolean | Children,
    ): ReactElement => <Body size="m">{children}</Body>,
  },
};
